import React, { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { Box, Skeleton, Stack } from "@mui/material";
import { useApi } from "@/client/api";
import { IStep, IThread } from "@/client/types";
import {
  accessTokenState,
  companyState,
  currentFileRefIdState,
  currentThreadIdState,
  messagesState,
  threadHistoryState,
} from "@/client/state";
import { nestMessages } from "@/client/utils/message";
import { useChatInteract } from "@/client/useChatInteractV2";
import { useLayoutMaxWidth } from "@/hooks/useLayoutMaxWidth";

import Page from "./Page";
import Chat from "@/components/organisms/chat";
import { GenAiContext } from "@/client/context";

const Skeletons: React.FC = () => {
  const layoutMaxWidth = useLayoutMaxWidth();

  return (
    <Box sx={{ display: "flex", flexDirection: "column", flexGrow: 1, gap: 2 }}>
      <Box sx={{ width: "100%", flexGrow: 1, overflow: "auto" }}>
        {[1, 2, 3].map((index) => (
          <Stack
            key={`thread-skeleton-${index}`}
            sx={{
              px: 2,
              gap: 4,
              mt: 5,
              flexDirection: "row",
              justifyContent: "center",
            }}
          >
            <Stack>
              <Skeleton width={50} />
              <Skeleton width={50} />
            </Stack>
            <Skeleton
              variant="rounded"
              sx={{
                maxWidth: layoutMaxWidth,
                width: "100%",
                height: 100,
              }}
            />
          </Stack>
        ))}
      </Box>
    </Box>
  );
};

export default function ThreadPage() {
  const { id } = useParams();
  const { clear } = useChatInteract();

  const [threadHistory, setThreadHistory] = useRecoilState(threadHistoryState);

  const setCurrentThreadId = useSetRecoilState(currentThreadIdState);
  const setCurrentFileRefId = useSetRecoilState(currentFileRefIdState);
  const setMessages = useSetRecoilState(messagesState);
  const [companyId, setCompanyId] = useRecoilState(companyState);

  const apiClient = useContext(GenAiContext);
  const accessToken = useRecoilValue(accessTokenState);

  const [isThreadLoaded, setIsThreadLoaded] = useState(false);

  const { error, isLoading, isValidating } = useApi<IThread>(
    id ? `/api/chat/thread/${id}` : null,
    {
      revalidateOnFocus: false,
      onSuccess: (response) => {
        const data = response.data ?? null;

        clear();
        if (data) {
          setMessages(nestMessages(data.history));
          setCurrentThreadId(data.thread_id);
          if (data.fileReference) setCurrentFileRefId(data.fileReference);
          setIsThreadLoaded(true); // ✅ Mark thread as fully loaded
        }
      },
      onError: () => {
        clear();
      },
    }
  );

  useEffect(() => {
    if (companyId && isThreadLoaded) {
      const fetchCompanyMessage = async () => {
        try {
          const res = await apiClient.getAccountReport(companyId, accessToken);

          const now = new Date().toISOString();
          const step = {
            id: "0",
            name: "Company Report",
            type: "assistant" as const,
            // content: res.data,
            content: `Ini merupakan data hasil generate report dari ${res.data.company_name}. 

[[file:${res.data.file_name}]]
            
Laporan ini disusun secara sistematis dengan tujuan memberikan gambaran menyeluruh mengenai kondisi perusahaan, menghadirkan insight yang relevan, serta mendukung proses analisis dan pengambilan keputusan strategis.

Apabila terdapat bagian yang perlu ditinjau lebih lanjut, memerlukan penjelasan tambahan, atau jika ada pertanyaan terkait informasi yang tercantum di dalam laporan, jangan ragu untuk menyampaikannya. Kami dengan senang hati siap memberikan klarifikasi maupun pendampingan sesuai kebutuhan Anda.`,
            createdAt: now,
          };

          setMessages((prev) => {
            const existsAtTop = prev.length > 0 && prev[0].role === "assistant";

            const normalizedStep: IStep = {
              ...step,
              role: "assistant", // Ensure role is present
            };

            return existsAtTop
              ? [...prev, normalizedStep]
              : [normalizedStep, ...prev];
          });
        } catch (error) {
          console.error("Failed to fetch company message:", error);
        }
      };

      fetchCompanyMessage();
    }
  }, [id, companyId, isThreadLoaded]);

  useEffect(() => {
    setIsThreadLoaded(false);
    if (threadHistory?.currentThreadId !== id) {
      setThreadHistory((prev) => {
        return { ...prev, currentThreadId: id };
      });
    }
  }, [id]);

  return (
    <Page companyId={companyId} setCompanyId={setCompanyId}>
      {!!error || isLoading || isValidating ? (
        <Skeletons />
      ) : (
        <Chat companyId={companyId} />
      )}
    </Page>
  );
}
